import type { BusinessType, Language } from '../../../shared/enums';
import { BUSINESS_TYPES, LANGUAGES } from '../../../shared/enums';
import type { Lead } from '../../../shared/types';
import { shell } from '../app';
import { createLead, getSnapshot, onStoreChange } from '../data/store';
import { timeAgo } from '../logic/dates';
import { lastVisitAt, recentLeads, searchLeads } from '../logic/pipeline';
import { btn, empty, field, input, leadTitle, row, segmented, stageTag } from '../ui/components';
import { h, replaceChildren, span } from '../ui/dom';
import { BUSINESS_TYPE_LABELS, LANGUAGE_LABELS } from '../ui/labels';

/** Quick-log step 1: search an existing lead (recent ones when empty) or create one inline. */
// ---------------------------------------------------------------- step 1: pick / create

export const pickStep = (host: HTMLElement, onPick: (lead: Lead) => void) => {
  let query = '';
  const list = h('div', { class: 'list' });
  const search = input({
    type: 'search',
    placeholder: 'Business, owner, street…',
    enterkeyhint: 'search',
    autocapitalize: 'words',
    class: 'input--search',
    onInput: (v) => {
      query = v;
      void renderList();
    },
  });

  const renderList = async () => {
    const snap = await getSnapshot();
    const last = lastVisitAt(snap.visits);
    const q = query.trim();
    const matches = q ? searchLeads(snap.leads, q).slice(0, 30) : recentLeads(snap.leads, snap.visits, 10);
    const exact = q && snap.leads.some((l) => l.business_name.toLowerCase() === q.toLowerCase());
    const items: HTMLElement[] = [];
    if (q && !exact) items.push(newLeadRow(q));
    if (!q) items.push(newLeadRow(''));
    if (!q && matches.length) items.push(h('h3', { class: 'list__label mono' }, 'Recent'));
    for (const lead of matches) {
      const lv = last.get(lead.id);
      items.push(
        row({
          title: leadTitle(lead),
          meta: [
            stageTag(lead.stage),
            lead.neighborhood ? ` · ${lead.neighborhood}` : '',
            lv ? ` · visited ${timeAgo(lv)}` : ' · never visited',
          ],
          onClick: () => onPick(lead),
          class: 'row--tap',
        }),
      );
    }
    if (q && !matches.length) items.push(empty(`No lead matches “${q}”.`, 'Create it above and keep going.'));
    replaceChildren(list, ...items);
  };

  const newLeadRow = (name: string) =>
    row({
      title: [span('row__plus', '+'), ' ', name ? `New lead “${name}”` : 'New lead'],
      meta: 'Create in a few taps, log the visit right after',
      onClick: () => showCreate(name),
      class: 'row--tap row--new',
      tone: 'accent',
    });

  const showCreate = (name: string) => {
    let draft: {
      business_name: string;
      business_type: BusinessType;
      neighborhood: string;
      phone: string;
      owner_name: string;
      owner_language: Language;
    } = {
      business_name: name,
      business_type: 'restaurant',
      neighborhood: '',
      phone: '',
      owner_name: '',
      owner_language: 'en',
    };
    const nameInput = input({
      value: name,
      maxlength: 120,
      placeholder: 'Business name',
      autocapitalize: 'words',
      required: true,
      onInput: (v) => (draft = { ...draft, business_name: v }),
    });
    const save = btn('Create & continue', {
      variant: 'primary',
      size: 'lg',
      class: 'w-full',
      onClick: async () => {
        const business_name = draft.business_name.trim();
        if (!business_name) {
          nameInput.focus();
          return;
        }
        save.disabled = true;
        try {
          const lead = await createLead({
            business_name,
            business_type: draft.business_type,
            neighborhood: draft.neighborhood.trim() || null,
            phone: draft.phone.trim() || null,
            owner_name: draft.owner_name.trim() || null,
            owner_language: draft.owner_language,
          });
          shell.toast('Lead created');
          onPick(lead);
        } catch (err) {
          shell.toast(`Could not save: ${err instanceof Error ? err.message : String(err)}`, {
            kind: 'error',
          });
          save.disabled = false;
        }
      },
    });
    replaceChildren(
      host,
      h(
        'div',
        { class: 'page page--create' },
        h(
          'div',
          { class: 'page__lead' },
          h('h2', { class: 'page__h2' }, 'New lead'),
          btn('Back to search', { variant: 'subtle', size: 'sm', onClick: () => showPick() }),
        ),
        field('Business name', nameInput),
        field(
          'Type',
          segmented<BusinessType>({
            options: BUSINESS_TYPES.map((t) => ({ value: t, label: BUSINESS_TYPE_LABELS[t] })),
            value: draft.business_type,
            onChange: (v) => (draft = { ...draft, business_type: v }),
            columns: 4,
            size: 'md',
          }),
        ),
        field(
          'Neighborhood',
          input({
            placeholder: 'e.g. Santa Ana',
            maxlength: 80,
            autocapitalize: 'words',
            onInput: (v) => (draft = { ...draft, neighborhood: v }),
          }),
        ),
        h(
          'div',
          { class: 'grid-2' },
          field(
            'Owner / contact',
            input({
              placeholder: 'Name',
              maxlength: 80,
              autocapitalize: 'words',
              onInput: (v) => (draft = { ...draft, owner_name: v }),
            }),
          ),
          field(
            'Phone',
            input({
              type: 'tel',
              inputmode: 'tel',
              placeholder: '714…',
              maxlength: 40,
              onInput: (v) => (draft = { ...draft, phone: v }),
            }),
          ),
        ),
        field(
          'Owner speaks',
          segmented<Language>({
            options: LANGUAGES.map((l) => ({ value: l, label: LANGUAGE_LABELS[l] })),
            value: draft.owner_language,
            onChange: (v) => (draft = { ...draft, owner_language: v }),
            columns: 3,
            size: 'md',
          }),
        ),
        h('div', { class: 'actionbar actionbar--inline' }, save),
      ),
    );
    setTimeout(() => nameInput.focus(), 30);
  };

  const showPick = () => {
    replaceChildren(
      host,
      h('div', { class: 'page page--pick' }, h('div', { class: 'searchbar' }, search), list),
    );
    search.value = query;
    void renderList();
    setTimeout(() => search.focus(), 30);
  };

  showPick();
  return onStoreChange(() => void renderList());
};
